import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { COLLAB_API_BASE, authFetch } from '@tl4k/frontend-shared';
import { Check, Copy, Download, Link2, Loader2, Trash2 } from 'lucide-react';

/**
 * Cartella condivisa degli elaborati di uno scope (sessione o percorso):
 * link corto ad accesso libero, QR, toggle abilita/sospendi, scadenza,
 * download zip. Generico per `artifactType`: il backend risolve gli adapter.
 */

export interface ShareFolderPanelProps {
  artifactType: string;
  scopeType: 'session' | 'project';
  scopeId: string;
  /** Origine dell'app che ospita la pagina pubblica `/s/<codice>` (es. https://riscontri.dominio). */
  publicBaseUrl: string;
  /** Etichetta umana per il testo (es. "SVG"). */
  itemsLabel?: string;
}

interface ShareDto {
  id: string;
  code: string;
  enabled: boolean;
  expires_at: string;
  state: 'active' | 'disabled' | 'expired';
}

/** 15/30/45/60 minuti, poi ore intere fino a 48 (stessa regola del server). */
const EXPIRY_OPTIONS: { minutes: number; label: string }[] = [
  ...[15, 30, 45, 60].map((m) => ({ minutes: m, label: `${m} minuti` })),
  ...Array.from({ length: 47 }, (_, i) => {
    const h = i + 2;
    return { minutes: h * 60, label: `${h} ore` };
  }),
];
const DEFAULT_MINUTES = 60;

async function qrDataUrl(text: string): Promise<string> {
  // @ts-ignore qrcode non ha tipi nel bundle condiviso
  const mod = await import('qrcode');
  const QR = mod.default || mod;
  return QR.toDataURL(text, { width: 176, margin: 1, errorCorrectionLevel: 'M' });
}

function remaining(expiresAt: string, now: number): string {
  const ms = new Date(expiresAt).getTime() - now;
  if (ms <= 0) return 'scaduto';
  const min = Math.ceil(ms / 60_000);
  if (min < 60) return `scade tra ${min} min`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  return `scade tra ${h} h${m ? ` ${m} min` : ''}`;
}

const ShareFolderPanel: React.FC<ShareFolderPanelProps> = ({
  artifactType,
  scopeType,
  scopeId,
  publicBaseUrl,
  itemsLabel = 'elaborati',
}) => {
  const [share, setShare] = useState<ShareDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [minutes, setMinutes] = useState(DEFAULT_MINUTES);
  const [copied, setCopied] = useState(false);
  const [qr, setQr] = useState('');
  const [now, setNow] = useState(() => Date.now());

  const link = share ? `${publicBaseUrl.replace(/\/+$/, '')}/s/${share.code}` : '';
  const base = `${COLLAB_API_BASE}/artifact-shares`;

  const readError = async (res: Response, fallback: string) => {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    return `${fallback} (${res.status}${body.error ? `: ${body.error}` : ''})`;
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const q = new URLSearchParams({ scope_type: scopeType, scope_id: scopeId, artifact_type: artifactType });
      const res = await authFetch(`${base}?${q}`);
      if (!res.ok) throw new Error(await readError(res, 'Impossibile leggere le condivisioni'));
      const data = (await res.json()) as { items: ShareDto[] };
      setShare(data.items[0] ?? null);
    } catch (e) {
      console.warn('[ShareFolderPanel] load:', e);
      setError(e instanceof Error ? e.message : 'Errore di rete');
    } finally {
      setLoading(false);
    }
  }, [artifactType, base, scopeId, scopeType]);

  useEffect(() => {
    setShare(null);
    void load();
  }, [load]);

  useEffect(() => {
    const t = window.setInterval(() => setNow(Date.now()), 20_000);
    return () => window.clearInterval(t);
  }, []);

  useEffect(() => {
    if (!link) {
      setQr('');
      return;
    }
    let cancelled = false;
    void qrDataUrl(link)
      .then((u) => !cancelled && setQr(u))
      .catch((e) => console.warn('[ShareFolderPanel] QR:', e));
    return () => {
      cancelled = true;
    };
  }, [link]);

  const call = async (fn: () => Promise<Response>, fallback: string): Promise<Response | null> => {
    setBusy(true);
    setError('');
    try {
      const res = await fn();
      if (!res.ok) throw new Error(await readError(res, fallback));
      return res;
    } catch (e) {
      console.warn('[ShareFolderPanel]', fallback, e);
      setError(e instanceof Error ? e.message : 'Errore di rete');
      return null;
    } finally {
      setBusy(false);
    }
  };

  const json = { 'Content-Type': 'application/json' };

  const create = async () => {
    const res = await call(
      () =>
        authFetch(base, {
          method: 'POST',
          headers: json,
          body: JSON.stringify({
            artifact_type: artifactType,
            scope_type: scopeType,
            scope_id: scopeId,
            expires_in_minutes: minutes,
          }),
        }),
      'Creazione link non riuscita',
    );
    if (res) setShare((await res.json()) as ShareDto);
  };

  const patch = async (body: Record<string, unknown>) => {
    if (!share) return;
    const res = await call(
      () => authFetch(`${base}/${share.id}`, { method: 'PATCH', headers: json, body: JSON.stringify(body) }),
      'Aggiornamento non riuscito',
    );
    if (res) setShare((await res.json()) as ShareDto);
  };

  const revoke = async () => {
    if (!share || !window.confirm('Revocare il link? Chi lo possiede non potrà più accedere.')) return;
    const res = await call(() => authFetch(`${base}/${share.id}`, { method: 'DELETE' }), 'Revoca non riuscita');
    if (res) setShare(null);
  };

  const downloadZip = async () => {
    const q = new URLSearchParams({ artifact_type: artifactType, scope_type: scopeType, scope_id: scopeId });
    const res = await call(() => authFetch(`${base}/export.zip?${q}`), 'Download zip non riuscito');
    if (!res) return;
    const blob = await res.blob();
    const cd = res.headers.get('Content-Disposition') ?? '';
    const name = /filename="([^"]+)"/.exec(cd)?.[1] ?? `${artifactType}.zip`;
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
    } catch (e) {
      console.warn('[ShareFolderPanel] clipboard:', e);
      window.prompt('Copia il link:', link);
    }
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1800);
  };

  const state = useMemo(() => {
    if (!share) return null;
    if (new Date(share.expires_at).getTime() <= now) return 'expired' as const;
    return share.enabled ? ('active' as const) : ('disabled' as const);
  }, [share, now]);

  const selectCls = 'border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white min-h-[44px]';
  const btn =
    'inline-flex items-center gap-2 px-4 min-h-[44px] rounded-lg text-sm font-semibold transition-colors disabled:opacity-50';

  return (
    <section
      aria-label="Cartella condivisa"
      className="rounded-xl border border-slate-200 bg-white p-4 flex flex-col gap-4"
    >
      <div className="flex flex-wrap items-center gap-2 justify-between">
        <h3 className="text-base font-bold text-slate-800 flex items-center gap-2 m-0">
          <Link2 size={18} aria-hidden="true" /> Cartella condivisa
        </h3>
        <button
          type="button"
          onClick={downloadZip}
          disabled={busy}
          className={`${btn} bg-slate-700 text-white hover:bg-slate-800`}
        >
          <Download size={16} aria-hidden="true" /> Scarica zip ({itemsLabel})
        </button>
      </div>

      {error && (
        <p role="alert" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2 m-0">
          {error}
        </p>
      )}

      {loading ? (
        <p className="text-sm text-slate-500 flex items-center gap-2 m-0">
          <Loader2 size={16} className="animate-spin" aria-hidden="true" /> Caricamento…
        </p>
      ) : !share ? (
        <div className="flex flex-wrap items-end gap-3">
          <label className="flex flex-col gap-1 text-sm text-slate-600">
            Il link resta valido per
            <select
              className={selectCls}
              value={minutes}
              onChange={(e) => setMinutes(Number(e.target.value))}
            >
              {EXPIRY_OPTIONS.map((o) => (
                <option key={o.minutes} value={o.minutes}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            onClick={create}
            disabled={busy}
            className={`${btn} bg-blue-600 text-white hover:bg-blue-700`}
          >
            Crea link condiviso
          </button>
          <p className="text-xs text-slate-500 basis-full m-0">
            Chiunque abbia il link vede gli elaborati con avatar e pseudonimo (mai nomi reali) e può scaricarli.
          </p>
        </div>
      ) : (
        <div className="flex flex-wrap gap-4">
          {qr && (
            <img
              src={qr}
              alt={`Codice QR del link ${link}`}
              width={176}
              height={176}
              className="border border-slate-200 rounded-lg bg-white"
            />
          )}
          <div className="flex-1 min-w-[260px] flex flex-col gap-3">
            <div className="flex gap-2">
              <input
                readOnly
                value={link}
                aria-label="Link condiviso"
                onFocus={(e) => e.currentTarget.select()}
                className="flex-1 min-w-0 border border-slate-300 rounded-lg px-3 py-2 text-sm font-mono min-h-[44px]"
              />
              <button
                type="button"
                onClick={copy}
                className={`${btn} bg-blue-600 text-white hover:bg-blue-700`}
              >
                {copied ? <Check size={16} aria-hidden="true" /> : <Copy size={16} aria-hidden="true" />}
                {copied ? 'Copiato' : 'Copia'}
              </button>
            </div>

            <p className="m-0 text-sm" aria-live="polite">
              {state === 'active' && (
                <span className="text-emerald-700 font-semibold">
                  Attivo · {remaining(share.expires_at, now)}
                </span>
              )}
              {state === 'disabled' && <span className="text-amber-700 font-semibold">Sospeso</span>}
              {state === 'expired' && (
                <span className="text-red-700 font-semibold">Scaduto · scegli una nuova durata per riattivarlo</span>
              )}
            </p>

            <div className="flex flex-wrap items-center gap-3">
              <label className="inline-flex items-center gap-2 text-sm min-h-[44px]">
                <input
                  type="checkbox"
                  checked={share.enabled}
                  disabled={busy}
                  onChange={(e) => void patch({ enabled: e.target.checked })}
                  className="w-5 h-5"
                />
                Condivisione attiva
              </label>
              <label className="inline-flex items-center gap-2 text-sm">
                Imposta durata da adesso
                <select
                  className={selectCls}
                  value=""
                  disabled={busy}
                  onChange={(e) => {
                    if (e.target.value) void patch({ expires_in_minutes: Number(e.target.value), enabled: true });
                  }}
                >
                  <option value="">Scegli…</option>
                  {EXPIRY_OPTIONS.map((o) => (
                    <option key={o.minutes} value={o.minutes}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </label>
              <button
                type="button"
                onClick={revoke}
                disabled={busy}
                className={`${btn} border border-red-300 text-red-700 hover:bg-red-50`}
              >
                <Trash2 size={16} aria-hidden="true" /> Revoca
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default ShareFolderPanel;
